'use client';

import { useState } from 'react';
import { cn } from '@/helpers/tailwind';
import Link from 'next/link';
import { Typography } from '@/atoms/00-ui/Typography';
import { Arrow } from '@/images/svg/Arrow';
import { TopInsightImage } from '@/molecules/00-ui/TopInsightImage';
import { ITopInsightsDesktopProps } from '@/organisms/00-ui/TopInsightsDesktop/index.types';

export const TopInsightsDesktop = ({ insights }: ITopInsightsDesktopProps) => {
  const [hoverIndex, setHoverIndex] = useState(0);

  return (
    <ul className="w-full h-full flex flex-col lg:flex-row overflow-hidden bg-white gap-x-[2px]">
      {insights.map(({ id, titleChunks, description, timing, tags, image, href }, index) => {
        const isHover = hoverIndex === index;

        return (
          <li
            key={id}
            onMouseOver={() => setHoverIndex(index)}
            className={cn(
              `h-full flex-grow transition-[flex-basis] duration-700 cursor-pointer relative 
              bg-linear-gradient-grey-blue delay-300 will-change-[flex-basis]`,
              {
                [`flex-[2] basis-[50%]`]: isHover,
                [`flex-[1] basis-[25%]`]: !isHover,
              },
            )}
          >
            <div
              className={cn(`absolute inset-0 w-full h-full bg-purple-14 opacity-0`, {
                [`opacity-100`]: isHover,
              })}
            />
            <Link
              className="flex p-2xl h-full relative z-10"
              href={href}
            >
              <div
                className={cn(
                  `w-full h-full grow-1 basis-0 duration-700 mr-0 transition-[flex-grow,flex-basis,margin-right]
                    relative overflow-hidden delay-300 will-change-[flex-grow,flex-basis,margin-right]
                    `,
                  {
                    [`basis-[35%] mr-[30px]`]: isHover,
                  },
                )}
              >
                <TopInsightImage
                  image={image}
                  tags={tags}
                />
              </div>
              <div
                className={cn(
                  `w-full h-full 
                  flex-1 basis-0 flex flex-col justify-between 
                  duration-500 transition-[flex-grow] delay-300 will-change-[flex-grow]`,
                  {
                    [`grow-1`]: isHover,
                  },
                )}
              >
                {titleChunks && (
                  <Typography
                    component="h3"
                    className={cn(
                      `text-black-10 font-monoItalic mobile:text-2xl line-clamp-4 black-10 mobile:leading-[29px]`,
                      {
                        [`text-white line-clamp-2`]: isHover,
                      },
                    )}
                  >
                    {titleChunks.join(' ')}
                  </Typography>
                )}

                <Typography
                  variant="body1"
                  component="p"
                  className={cn(`text-purple-3 mobile:text-base h-0 overflow-hidden delay-300`, {
                    [`h-auto line-clamp-2`]: isHover,
                  })}
                >
                  {description}
                </Typography>

                <div className="flex gap-x-[6px] items-center self-end">
                  <span
                    className={cn(` mobile:text-base text-black-10`, {
                      [`text-green-7`]: isHover,
                    })}
                  >
                    {timing}
                  </span>

                  <Arrow
                    variant={`${isHover ? 'home-green' : 'primary'}`}
                    className="group-hover:translate-x-[5px] duration-300"
                  />
                </div>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};
